import React, { useState, useEffect, useCallback } from 'react';
import BuyerPortalLayout from '../../components/buyer/BuyerPortalLayout';
import { fetchBuyerInvoices, downloadBuyerInvoicePdf } from '../../lib/buyerApi';
import { DEFAULT_CURRENCY, formatMoney } from '../../components/marketplace/cartUtils';

const BuyerInvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchBuyerInvoices();
      setInvoices(res.invoices || res.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (inv: any) => {
    setDownloadingId(inv.id);
    try { await downloadBuyerInvoicePdf(inv.id, inv.invoice_number || `INV-${inv.id}.pdf`); }
    catch (err: any) { alert(err.message || 'Download failed'); }
    finally { setDownloadingId(null); }
  };

  return (
    <BuyerPortalLayout title="Invoices">
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <p className="text-5xl mb-4">📄</p>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No invoices yet</h3>
          <p className="text-gray-500 text-sm">Invoices will appear here once orders are fulfilled.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100"><tr>
              <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Invoice</th>
              <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Amount</th>
              <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Status</th>
              <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Due Date</th>
              <th className="px-5 py-3 text-left font-bold text-gray-500 text-xs uppercase">Action</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-50">
              {invoices.map((inv: any) => (
                <tr key={inv.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-mono text-xs font-medium">{inv.invoice_number || `INV-${inv.id}`}</td>
                  <td className="px-5 py-3 font-bold">{formatMoney(Number(inv.total_amount) || 0, inv.currency || DEFAULT_CURRENCY)}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                      inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>{inv.status || 'pending'}</span>
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs">{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => handleDownload(inv)} disabled={downloadingId === inv.id}
                      className="px-3 py-1.5 text-xs font-bold text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-50 disabled:opacity-50 transition">
                      {downloadingId === inv.id ? '...' : '⬇ PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </BuyerPortalLayout>
  );
};

export default BuyerInvoicesPage;
